import React, { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import Checkbox from './Checkbox';

interface Option {
    id: string;
    label: string;
    name: string;
}

interface Props {
    name: string; // group field name used to register group-level validation
    options: Option[];
    className?: string;
    validationMessage?: string;
}

const shallowArrayEqual = (a: any[], b: any[]) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
};

const CheckboxGroup: React.FC<Props> = ({ name, options = [], className = '', validationMessage }) => {
    const { register, getValues, trigger, unregister, formState: { errors } } = useFormContext();

    // Keep refs to latest options and validationMessage so validateFn doesn't capture stale values
    const optionsRef = useRef<Option[]>(options);
    optionsRef.current = options;

    const validationMessageRef = useRef<string | undefined>(validationMessage);
    validationMessageRef.current = validationMessage;

    // Register a virtual/group field that validates at least one of the option fields is truthy
    useEffect(() => {
        if (!name) return;

        const validateFn = () => {
            try {
                const values = optionsRef.current.map(opt => getValues(opt.name));
                return values.some(Boolean) || validationMessageRef.current || 'At least one option must be selected.';
            } catch (e) {
                // Returning true treats validation as passing to avoid blocking due to unexpected errors
                return true;
            }
        };

        // register once on mount; cleanup on unmount
        register(name, { validate: validateFn });
        return () => {
            try { unregister(name); } catch (e) {
                // CheckboxGroup unregister error
            }
        };
    // register/unregister and name are stable deps; avoid depending on options or validationMessage to prevent re-register loops
    }, [name, register, unregister, getValues]);

    // Watch the group field value, which should be an array of booleans
    const watchedValues = useWatch({
        name,
        defaultValue: options.map(() => false),
    });

    const prevWatchedRef = useRef<any[]>(
        Array.isArray(watchedValues) ? [...watchedValues] : [watchedValues]
    );

    useEffect(() => {
        if (!name) return;

        const prev = prevWatchedRef.current || [];
        const curr = Array.isArray(watchedValues) ? watchedValues : [watchedValues];

        // Only trigger validation when the watched values actually change (shallow comparison)
        if (!shallowArrayEqual(prev, curr)) {
            prevWatchedRef.current = [...curr];
            // Trigger validation for the group asynchronously to avoid nested updates
            Promise.resolve().then(() => trigger(name));
        }
    }, [watchedValues, name, trigger]);

    const groupError = errors?.[name]?.message as string | undefined;

    return (
        <div className={className}>
            {options.map((opt) => (
                <Checkbox
                    key={opt.id}
                    id={opt.id}
                    name={opt.name}
                    label={opt.label}
                    // child checkboxes should not show the group error themselves
                    showGroupError={false}
                />
            ))}
            {groupError && (
                <div className="text-red fs-14px">{groupError}</div>
            )}
        </div>
    );
};

export default CheckboxGroup;
// ...existing code...